// src/pages/ChatPage.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Users, Smile } from "lucide-react";
import EmojiPicker from "emoji-picker-react";


const WS_URL =
  import.meta?.env?.VITE_WS_URL ||
  process.env.REACT_APP_WS_URL ||
  "ws://10.3.102.251:4000/socket/websocket";

function getStoredUser() {
  try {
    const raw = localStorage.getItem("user") || "{}";
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

const CHAT_STORAGE_PREFIX = "uconnect:chat:";
const CHANNEL_KEY = (userDisplay) => `${CHAT_STORAGE_PREFIX}chat:lobby:${userDisplay}`;

const ChatPage = () => {
  // ---------- State ----------
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [onlineUsers, setOnlineUsers] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isJoined, setIsJoined] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // ---------- Refs ----------
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const activityTimeoutRef = useRef(null);
  const reconnectRef = useRef({ attempts: 0, timer: null });
  const shuttingDownRef = useRef(false);
  const emojiBtnRef = useRef(null);
  const emojiPanelRef = useRef(null);

  // ---------- Derived ----------
  const storedUser = useMemo(() => getStoredUser(), []);
  const currentUserAvatar = storedUser?.profileImage?.image || null;

  // ---------- Helpers ----------
  const isOpen = () =>
    socketRef.current && socketRef.current.readyState === WebSocket.OPEN;

  const safeSend = (frame) => {
    if (isOpen()) socketRef.current.send(JSON.stringify(frame));
  };

  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load user + cached chat
  useEffect(() => {
    const user = storedUser;
    const display = user?.name || user?.username || user?.email;
    if (!display) {
      navigate("/login", { replace: true });
      return;
    }
    setCurrentUser(display);

    const key = CHANNEL_KEY(display);
    try {
      const cached = JSON.parse(localStorage.getItem(key) || "[]");
      if (Array.isArray(cached)) setMessages(cached);
    } catch {
      /* ignore */
    }
  }, [navigate, storedUser]);

  // Persist messages
  useEffect(() => {
    if (!currentUser) return;
    const key = CHANNEL_KEY(currentUser);
    const trimmed = messages.slice(-500);
    try {
      localStorage.setItem(key, JSON.stringify(trimmed));
    } catch { }
  }, [messages, currentUser]);

  // Presence tracking
  const trackActivity = useCallback(() => {
    if (!isJoined) return;

    if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);

    safeSend({
      topic: "chat:lobby",
      event: "user_activity",
      payload: { status: "active" },
      ref: Date.now().toString(),
    });

    activityTimeoutRef.current = setTimeout(() => {
      safeSend({
        topic: "chat:lobby",
        event: "user_activity",
        payload: { status: "inactive" },
        ref: Date.now().toString(),
      });
    }, 120000);
  }, [isJoined]);

  // Handle frames
  const handleSocketMessage = (data) => {
    const { topic, event, payload } = data;

    switch (event) {
      case "phx_reply":
        if (payload?.status === "ok" && topic === "chat:lobby") {
          setIsJoined(true);
          trackActivity();
        }
        break;

      case "new_message": {
        const msg = {
          id: Date.now(),
          username: payload?.username,
          message: payload?.message,
          timestamp: new Date().toLocaleTimeString(),
          profileImage: payload?.profileImage || null,
        };
        setMessages((prev) => [...prev, msg]);
        trackActivity();
        break;
      }

      case "presence_state":
        setOnlineUsers(payload || {});
        break;

      case "presence_diff":
        setOnlineUsers((prev) => {
          const updated = { ...prev };
          if (payload?.leaves) {
            Object.keys(payload.leaves).forEach((u) => delete updated[u]);
          }
          if (payload?.joins) Object.assign(updated, payload.joins);
          return updated;
        });
        break;

      default:
        break;
    }
  };

  // Connect with backoff
  const connect = useCallback(() => {
    if (!currentUser || shuttingDownRef.current) return;

    if (
      socketRef.current &&
      socketRef.current.readyState !== WebSocket.CLOSED &&
      socketRef.current.readyState !== WebSocket.CLOSING
    ) {
      return;
    }

    try {
      const ws = new WebSocket(WS_URL);
      socketRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        reconnectRef.current.attempts = 0;

        safeSend({
          topic: "chat:lobby",
          event: "phx_join",
          payload: { username: currentUser },
          ref: Date.now().toString(),
        });
      };

      ws.onmessage = (evt) => {
        try {
          handleSocketMessage(JSON.parse(evt.data));
        } catch { }
      };

      ws.onclose = () => {
        setIsConnected(false);
        setIsJoined(false);
        clearTimeout(activityTimeoutRef.current);
        socketRef.current = null;

        if (shuttingDownRef.current) return;

        const n = Math.min(reconnectRef.current.attempts + 1, 6);
        reconnectRef.current.attempts = n;
        const delay = Math.floor(500 * 2 ** n); // 1s..32s
        clearTimeout(reconnectRef.current.timer);
        reconnectRef.current.timer = setTimeout(connect, delay);
      };

      ws.onerror = () => setIsConnected(false);
    } catch (err) {
      console.error("WebSocket connect error:", err);
    }
  }, [currentUser]);

  // Auto-connect + visibility nudge
  useEffect(() => {
    if (!currentUser) return;

    shuttingDownRef.current = false;
    connect();

    const reconnectNow = () => connect();
    const onVisible = () => {
      if (!document.hidden) reconnectNow();
    };

    window.addEventListener("online", reconnectNow);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.removeEventListener("online", reconnectNow);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [currentUser, connect]);

  // Global UI activity listeners
  useEffect(() => {
    if (!isJoined) return;
    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => document.addEventListener(e, trackActivity, true));
    return () => {
      events.forEach((e) => document.removeEventListener(e, trackActivity, true));
      clearTimeout(activityTimeoutRef.current);
    };
  }, [isJoined, trackActivity]);

  // Graceful leave
  useEffect(() => {
    return () => {
      shuttingDownRef.current = true;
      clearTimeout(reconnectRef.current.timer);
      clearTimeout(activityTimeoutRef.current);

      if (isOpen()) {
        safeSend({
          topic: "chat:lobby",
          event: "phx_leave",
          payload: {},
          ref: Date.now().toString(),
        });
      }

      try {
        socketRef.current?.close();
      } catch { }
      socketRef.current = null;
    };
  }, []);

  // Close emoji picker on outside click
  useEffect(() => {
    if (!showEmojiPicker) return;
    const onDocClick = (e) => {
      if (
        emojiBtnRef.current?.contains(e.target) ||
        emojiPanelRef.current?.contains(e.target)
      ) return;
      setShowEmojiPicker(false);
    };
    document.addEventListener("mousedown", onDocClick, true);
    return () => document.removeEventListener("mousedown", onDocClick, true);
  }, [showEmojiPicker]);

  // Send message
  const sendMessage = () => {
    if (!newMessage.trim() || !isJoined) return;
    safeSend({
      topic: "chat:lobby",
      event: "new_message",
      payload: {
        username: currentUser,
        message: newMessage.trim(),
        profileImage: currentUserAvatar || undefined,
      },
      ref: Date.now().toString(),
    });
    setNewMessage("");
    setShowEmojiPicker(false);
    trackActivity();
  };

  // Resolve avatar
  const getMessageAvatar = (msg) => {
    if (msg.profileImage) return { type: "img", src: msg.profileImage };
    if (msg.username === currentUser && currentUserAvatar)
      return { type: "img", src: currentUserAvatar };
    const initial = (msg.username || "").trim().charAt(0).toUpperCase() || "?";
    return { type: "initials", text: initial };
  };

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4 flex-grow"
      style={{ minHeight: "500px", height: "calc(100vh - 280px)" }}
    >
      {/* Online Users */}
      <div
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-md transition-all duration-500 delay-200 flex flex-col h-full ${isJoined ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
      >
        <div className="rounded-t-lg px-3 py-2 bg-black dark:bg-yellow-500 text-white dark:text-black border-b flex items-center justify-between">
          <h3 className="text-sm font-bold flex items-center">
            <Users className="w-4 h-4 mr-2" />
            Online Users
          </h3>
          <span className="text-xs opacity-80">{Object.keys(onlineUsers).length} online</span>
        </div>

        <div className="p-3 flex-1 overflow-y-auto space-y-2">
          {Object.entries(onlineUsers).map(([user, data]) => {
            const isActive = data?.metas?.[0]?.status !== "inactive";
            return (
              <div
                key={user}
                className="flex items-center justify-between rounded-md px-3 py-2 bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700/40"
              >
                <div className="flex items-center">
                  <span
                    className={`w-2.5 h-2.5 rounded-full mr-2 ${isActive ? "bg-green-400" : "bg-gray-400"
                      }`}
                  />
                  <span className={`text-sm ${isActive ? "text-gray-900 dark:text-white" : "text-gray-500"}`}>
                    {user}
                    {user === currentUser && " (You)"}
                  </span>
                </div>
                <span className="text-[10px] uppercase tracking-wide text-gray-400">
                  {isActive ? "Active" : "Idle"}
                </span>
              </div>
            );
          })}
          {Object.keys(onlineUsers).length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
              Nobody is online yet.
            </div>
          )}
        </div>
      </div>

      {/* Chat */}
      <div
        className={`lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-md transition-all duration-500 delay-200 flex flex-col h-full ${isConnected ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
      >
        <div className="rounded-t-lg px-3 py-2 bg-black dark:bg-yellow-500 text-white dark:text-black border-b flex items-center justify-between">
          <h3 className="text-sm font-bold">U-Connect Chat</h3>
          <div className="flex items-center gap-2">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${isConnected ? "bg-green-400" : "bg-red-400"}`} />
            <span className="text-xs">{isConnected ? "Connected" : "Disconnected"}</span>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-gray-400 mt-8">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-60" />
              <p className="text-sm">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isSelf = msg.username === currentUser;
              const avatar = getMessageAvatar(msg);

              return (
                <div
                  key={msg.id}
                  className={`flex items-end gap-2 ${isSelf ? "justify-end" : "justify-start"}`}
                >
                  {/* Avatar */}
                  {!isSelf &&
                    (avatar.type === "img" ? (
                      <img
                        src={avatar.src}
                        alt={`${msg.username} avatar`}
                        className="w-6 h-6 rounded-full border border-gray-600 object-cover"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gray-300 text-gray-800 flex items-center justify-center text-[9px] font-semibold border border-gray-600">
                        {avatar.text}
                      </div>
                    ))}

                  {/* Sleek Bubble */}
                  <div
                    className={`max-w-[70%] px-2 py-1 rounded-md text-xs border border-gray-600 shadow-sm leading-tight ${isSelf ? "bg-yellow-400 text-black" : "bg-white text-black"
                      }`}
                  >
                    {!isSelf && (
                      <div className="text-[9px] opacity-70 mb-0.5">{msg.username}</div>
                    )}
                    <div
                      className={`break-words whitespace-pre-wrap`}
                    >
                      {msg.message}
                    </div>
                    <div className="text-[8px] opacity-60 mt-0.5">{msg.timestamp}</div>
                  </div>

                  {/* Avatar (self) */}
                  {isSelf &&
                    (avatar.type === "img" ? (
                      <img
                        src={avatar.src}
                        alt="You"
                        className="w-6 h-6 rounded-full border border-gray-600 object-cover"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gray-300 text-gray-800 flex items-center justify-center text-[9px] font-semibold border border-gray-600">
                        {avatar.text}
                      </div>
                    ))}
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input + Emoji */}
        <div className="px-3 pb-3 relative">
          <div className="flex gap-2 items-center">
            {/* Emoji Button */}
            <button
              ref={emojiBtnRef}
              type="button"
              onClick={() => setShowEmojiPicker((prev) => !prev)}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Add emoji"
            >
              <Smile className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </button>

            {/* Input */}
            {/* Input */}
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
                // Shift+Enter will naturally insert a newline in textarea,
                // so no need to handle it specially
              }}
              placeholder="Type your message..."
              className="flex-1 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700/40 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/60 resize-none"
              rows={1}
              disabled={!isConnected}
            />


            {/* Send */}
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim() || !isConnected}
              className="px-4 py-2 rounded-lg bg-black text-white dark:bg-yellow-500 dark:text-black disabled:opacity-50"
              title="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div
              ref={emojiPanelRef}
              className="absolute bottom-14 left-3 z-50"
            >
              <EmojiPicker
                onEmojiClick={(emojiData) => {
                  setNewMessage((prev) => prev + (emojiData.emoji || ""));
                  // keep panel open for multi-insert; close if you prefer:
                  // setShowEmojiPicker(false);
                }}
                theme={
                  document.documentElement.classList.contains("dark")
                    ? "dark"
                    : "light"
                }
                searchDisabled
                skinTonesDisabled
                lazyLoadEmojis
                previewConfig={{ showPreview: false }}
                width={280}
                height={320}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
